/**
 * TopBar Navigation Component
 * Top navigation bar with search, notifications, and user menu
 */
'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ThemeToggle } from '@/components/ThemeToggle';
import { GlobalSearchBar } from '@/components/search/GlobalSearchBar';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { useAuth } from '@/hooks/useAuth';
import { clearAuthStorage } from '@/lib/api/authSession';

interface TopBarProps {
  sidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
}

export function TopBar({ sidebarCollapsed, onToggleSidebar }: TopBarProps) {
  const router = useRouter();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Real user (with org memberships) + the tenant switcher come from useAuth.
  const { user: authUser, switchOrganization } = useAuth();
  const [switching, setSwitching] = useState(false);
  const organizations = authUser?.organizations ?? [];
  const currentOrgId = authUser?.current_organization_id ?? authUser?.org_id;
  const currentOrgName = organizations.find((o) => o.id === currentOrgId)?.name;

  const handleSwitchOrg = async (orgId: string) => {
    if (switching || orgId === currentOrgId) return;
    setSwitching(true);
    const res = await switchOrganization(orgId);
    // On success the hook triggers a full reload; only reset state on failure.
    if (!res.success) setSwitching(false);
  };

  // Get user from localStorage
  const [user, setUser] = useState<{ name?: string; email?: string } | null>(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch {
        setUser({ email: 'User' });
      }
    }
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    // Clear ALL auth keys (incl. impersonation backups). The old code removed 'auth_token' — a
    // key that is never written — so the real 'access_token' was left behind and the user stayed
    // logged in.
    clearAuthStorage();
    router.push('/login');
  };

  return (
    <header
      className={`fixed top-0 right-0 z-30 h-20 bg-white dark:bg-brand-dark-secondary/95 backdrop-blur-md border-b border-gray-200 dark:border-white/5 transition-all duration-300 ${
        sidebarCollapsed ? 'left-16' : 'left-64'
      }`}
    >
      <div className="h-full px-8 flex items-center justify-between gap-4">
        {/* Left side - hamburger menu and search */}
        <div className="flex items-center gap-4 flex-1">
          <button
            onClick={onToggleSidebar}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 lg:hidden"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Global Search */}
          <div className="hidden sm:block flex-1 max-w-xl">
            <GlobalSearchBar />
          </div>
        </div>

        {/* Right side - actions */}
        <div className="flex items-center gap-4 md:gap-6">
          {/* PRO PLAN Pill */}
          <button className="hidden md:flex items-center gap-2 bg-gradient-to-r from-brand-orange to-red-600 px-4 py-1.5 rounded-full text-xs font-bold text-white shadow-[0_0_15px_rgba(255,107,0,0.4)] hover:shadow-[0_0_25px_rgba(255,107,0,0.6)] transition-all transform hover:scale-105">
            <span className="animate-pulse">●</span> PRO PLAN
          </button>

          <div className="h-6 w-px bg-gray-200 dark:bg-white/10 hidden md:block" />

          {/* Theme toggle */}
          <ThemeToggle />

          {/* Help button */}
          <button className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>

          {/* Notifications */}
          <NotificationBell />

          {/* User menu */}
          <div className="relative flex items-center gap-3 pl-2 cursor-pointer group" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-3"
            >
              <div className="text-right hidden md:block">
                <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-brand-orange transition-colors">
                  {user?.name || 'User'}
                </p>
                <p className="text-xs text-gray-500 truncate max-w-[12rem]">{currentOrgName || 'Administrator'}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-gradient-to-tr from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 p-[1px] shadow-lg group-hover:shadow-brand-orange/20 transition-all">
                <div className="h-full w-full rounded-full bg-gray-100 dark:bg-brand-dark flex items-center justify-center text-gray-900 dark:text-white font-medium">
                  {user?.name?.[0] || user?.email?.[0] || 'U'}
                </div>
              </div>
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-64 max-h-[calc(100vh-6rem)] overflow-y-auto bg-white/95 dark:bg-brand-dark-secondary/95 backdrop-blur-xl rounded-xl shadow-lg border border-gray-200 dark:border-white/10 py-1 z-50 animate-fade-in-up">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-white/5">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {authUser?.full_name || authUser?.name || user?.name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {authUser?.email || user?.email || ''}
                  </p>
                </div>

                {/* Organization (tenant) switcher — only when the user belongs to more than one */}
                {organizations.length > 1 && (
                  <div className="px-2 py-2 border-b border-gray-200 dark:border-white/5">
                    <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      Switch workspace
                    </p>
                    {organizations.map((org) => {
                      const isCurrent = org.id === currentOrgId;
                      return (
                        <button
                          key={org.id}
                          onClick={() => handleSwitchOrg(org.id)}
                          disabled={switching || isCurrent}
                          className={`flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors ${
                            isCurrent
                              ? 'font-medium text-gray-900 dark:text-white'
                              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                          } ${switching && !isCurrent ? 'cursor-wait opacity-50' : ''}`}
                        >
                          <span className="truncate">{org.name}</span>
                          {isCurrent && (
                            <svg className="h-4 w-4 shrink-0 text-brand-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                <Link
                  href="/settings/profile"
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors"
                  onClick={() => setUserMenuOpen(false)}
                >
                  Profile
                </Link>
                <Link
                  href="/settings/brand"
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors"
                  onClick={() => setUserMenuOpen(false)}
                >
                  Settings
                </Link>
                <Link
                  href="/settings/billing"
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors"
                  onClick={() => setUserMenuOpen(false)}
                >
                  Billing
                </Link>
                <hr className="my-1 border-gray-200 dark:border-white/5" />
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default TopBar;
